import { AxiosResponse } from 'axios'
import { actionTree, getterTree, mutationTree } from 'typed-vuex'
import jsona, { JsonaModel } from '~/lib/jsona'
import { formatToUSDCurrency, withdrawalPeriod } from '~/lib/utils/format'
import { setState } from '~/lib/utils/store'
import { getKeys } from '~/lib/utils/utils'
import { API } from '~/types/api'
import { Store } from '~/types/store'

interface Package {
  packageId: number;
  name: string;
  price: string;
  pricePerMonth: number;
  discountPrice: string;
  recurrentPriceMessage: string;
  billingAgreement: string;
  expirationType: string;
  expirationPeriodType: string;
  expirationPeriodValue: number;
  paypalExpirationType: string;
  contactUsLink: boolean;
  buttonText: string;
  buttonCSS: string;
  displayMessage: string;
  displayDescriptionItems: string[];
  displaySuccessMessage: string;
  isMarked: boolean;
  isContactUs: boolean;
  isFreemium: boolean;
  isFreeTrial: boolean;
  freeTrialPeriodText: string;
}

interface PaymentDetails {
  convertToFreemiumEnabled: boolean;
  disclaimerEnabled: boolean;
  disclaimerText: string;
  popupTitle: string;
  packages: Package[];
  paymentMethodName: 'Stripe' | 'Braintree';
  stripePublishableKey: string;
}

export const state = () => ({
  membership: null as Store.Membership | null,
  profile: null as Store.Profile | null,
  mailbox: null as Store.Mailbox | null,
  paymentDetails: null as PaymentDetails | null,
  isContinueWithFreemiumLoading: false,
  isContinueWithFreemiumFailed: false,
  accountConfigAutoApplication: null as { enabled: boolean } & JsonaModel,
  utid: null as string
})

export const getters = getterTree(
  state,
  {
    unreadInbox({ mailbox }) {
      return mailbox?.inbox?.unread || null
    },
    isMember(...args) {
      return args[2].auth.user?.membership
    },
    isSubmittedScholarshipsRoute(...args) {
      return args[2].route?.path?.indexOf('/scholarships/submitted') === 0
    },
    isUSA({ profile }) {
      return profile?.country?.name === 'USA'
    },
    accountId(...args) {
      return Number(args[2].auth.user.id)
    },
    firstName({ profile }) {
      return profile ? profile.firstName : ''
    },
    lastName({ profile }) {
      return profile ? profile.lastName : ''
    },
    mailbox({ mailbox }) {
      return mailbox || null
    },
    isFreemium(...args) {
      const membership = args[2].auth.user ? args[2].auth.user.membership : null
      return !!membership && membership.freemium
    },
    isMaintenanceMode(...args) {
      if (!args[2].auth.user) { return false }
      const membership = args[2].auth.user.membership
      return (!!membership && membership.freeTrial && !!membership.isMaintenanceMode) ||
        (!!membership && !membership.freeTrial && !!membership.isMaintenanceModeHasEffect)
    },
    isPendingMaintenanceMode({ membership }) {
      return !!membership && !!membership.isMaintenanceMode && !membership.isMaintenanceModeHasEffect && !membership.freeTrial && !membership.isFreemium
    },
    /**
     * @deprecated This getter makes an incorrect check, use isActivePro instead.
     * @todo Remove this getter after all usages are replaced with isActivePro.
     */
    isPro(_state, { isMember, isFreemium, isMaintenanceMode }) {
      if (isMember === null) { return true }
      return isMember && !isFreemium && !isMaintenanceMode
    },
    isActivePro(_state, _getters, { auth: { user: { membership } } }) {
      return !!membership && !membership.freemium && !membership.isMaintenanceModeHasEffect
    },
    paymentMethod({ paymentDetails }) {
      return paymentDetails?.paymentMethodName
        ? paymentDetails.paymentMethodName
        : ''
    },
    isDiscountApplied({ membership }, { isMaintenanceMode }) {
      return membership?.isMember && !!membership.discountRequestedAt && !isMaintenanceMode
    },
    maintenanceWithdrawalPeriodMessage({ membership }) {
      return (price: number | string) => {
        let message = 'Billed '
        const formattedPrice = formatToUSDCurrency(Number(price) * membership.expirationValue)
        const period = withdrawalPeriod(membership.expirationPeriodType, membership.expirationValue)
        message += formattedPrice
        if (membership.expirationValue > 1) {
          message += ' every'
        }
        message += (' ' + period)
        return message
      }
    }
  }
)

export const mutations = mutationTree(
  state,
  {
    setState,
    /**
     * @deprecated
     */
    readMail(state) {
      if (!state?.mailbox?.inbox) { return }

      const inbox = state.mailbox.inbox

      // @ts-ignore
      if (inbox.unread > 0) {
        // @ts-ignore
        inbox.unread = inbox.unread - 1
      }

      if (inbox.read < inbox.total) {
        inbox.read = inbox.read + 1
      }
    }
  }
)

export const actions = actionTree(
  { state, getters, mutations },
  {
    setAccount({ state, commit }, { resources }): Promise<API.AccountInfo> {
      return Promise.all([
        this.$api.account.accountInfo({ fields: resources }),
        this.$auth.fetchUser()
      ]).then(([res]) => {
        const accountInfo: API.AccountInfo = res.data.data as API.AccountInfo

        getKeys(accountInfo).forEach((key) => {
          this.app.$accessor.account.setState({ stateName: key, data: accountInfo[key] })
        })

        return accountInfo
      })
    },

    loginAccount(store, credentials): Promise<AxiosResponse<any, any>> {
      return this.$api.account.login(credentials)
        .then((res) => {
          this.$mixpanel.identifyUser(res.data.data.accountId)
          this.$mixpanel.track(this.$mixpanel.events.EMAIL_LOGIN, {
            [this.$mixpanel.props.ACCOUNT_ID]: res.data.data.accountId
          })
          return res
        })
    },

    updateProfile({ state, commit, rootState: { auth } }, { data, useNewEndpoint = false }): Promise<AxiosResponse<any, any>> {
      const { profileUpdateJsonApi, profileUpdate } = this.$api.account
      const apiMethod = useNewEndpoint ? profileUpdateJsonApi : profileUpdate
      const idArgumentName = useNewEndpoint ? 'accountId' : 'id'

      return apiMethod({ [idArgumentName]: auth.user.id }, data)
        .then((response) => {
          if (response.status === 200 && response.data.data) {
            if (!state.profile) {
              commit('setState', { stateName: 'profile', data: {} })
            }

            Object.keys(response.data.data).forEach((field) => {
              commit('setState', { stateName: `profile.${field}`, data: response.data.data[field] })
            })

            // Removed commented out code here to fulfill SonarCloud rules.
            // If you need it it was last present in commit f17882197567381af3534bccb141d65198747ba1.
          }

          return response
        })
    },

    updateAccount({ rootState: { auth } }, data): Promise<AxiosResponse<any, any>> {
      return this.$api.account.accountUpdate({ accountId: auth.user.id }, data)
    },

    async getResourceValue({ state, dispatch }, { resourceName, resourceFields }): Promise<any> {
      if (!state[resourceName]) { await dispatch('setAccount', [resourceName]) }

      return Promise.resolve(resourceFields.reduce((acc, name) => {
        acc[name] = state[resourceName][name]
        return acc
      }, {}))
    },

    subscriptionCancel({ dispatch }, id): Promise<any> {
      return this.$api.subscription.cancel({ id }, {})
        .then((response) => {
          if (response && response.status === 200) {
            return dispatch('setAccount', { resources: ['membership'] })
          }

          return response
        })
    },

    applyDiscount({ dispatch, state: { membership: { isMaintenanceMode, subscriptionId } } }): Promise<any> {
      return this.$api.subscription.applyDiscount()
        .then((res) => {
          if (!isMaintenanceMode) { return res }
          return this.$api.subscription.updateMaintenanceMode({ id: subscriptionId }, {
            data: {
              attributes: {
                isMaintenanceMode: 0
              }
            }
          })
        })
        .then((response) => {
          if (response && response.status === 200) {
            return dispatch('setAccount', { resources: ['membership'] })
          }

          return response
        })
    },

    setPaymentDetails(): Promise<void> {
      return this.$api.account.setPackages()
        .then((res: Axios.AxiosResponse & { rawRes: any }) => {
          this.app.$accessor.account.setState({
            stateName: 'paymentDetails',
            data: res.rawRes.data
          })
        })
    },

    toggleMaintenanceMode({ state, dispatch }, { isMaintenanceMode }): Promise<any> {
      const data = {
        data: {
          attributes: {
            isMaintenanceMode
          }
        }
      }

      return this.$api.subscription.updateMaintenanceMode({ id: state.membership.subscriptionId }, data)
        .then(() => dispatch('setAccount', { resources: ['membership'] }))
    },

    async setFreemiumSubscription(): Promise<AxiosResponse<any, any>> {
      const response = await this.$api.subscription.setFreemium()
      return response?.data?.data?.freemium
    },

    async continueWithFreemium({ state, commit }) {
      commit('setState', { stateName: 'isContinueWithFreemiumFailed', data: false })
      if (state.isContinueWithFreemiumLoading) { return }
      commit('setState', { stateName: 'isContinueWithFreemiumLoading', data: true })

      try {
        const isFreemiumSetSuccessfully = await this.app.$accessor.account.setFreemiumSubscription()
        if (!isFreemiumSetSuccessfully) { throw new Error('You are already a Freemium user') }
        window.location.href = '/scholarships'
      } catch (err) {
        // eslint-disable-next-line no-console
        this.app.context.isDev && console.warn(err)
        commit('setState', { stateName: 'isContinueWithFreemiumFailed', data: true })
      } finally {
        commit('setState', { stateName: 'isContinueWithFreemiumLoading', data: false })
      }
    },

    async fetchAutoApplicationSetting({ commit }) {
      const { data: { data } } = await this.$api.accountConfigAutoApplication.get({
        accountId: this.$auth?.user?.id
      })

      commit('setState', { stateName: 'accountConfigAutoApplication', data })
    },

    async storeAutoApplicationSetting({ state, commit }, enabled: boolean) {
      const accountConfigAutoApplication = jsona.only(
        state.accountConfigAutoApplication,
        ['enabled']
      )

      accountConfigAutoApplication.enabled = enabled

      const response = await this.$api.accountConfigAutoApplication.set({
        accountId: this.$auth?.user?.id
      }, jsona.serialize({ stuff: accountConfigAutoApplication }))
      const { data: { data } } = response

      commit('setState', { stateName: 'accountConfigAutoApplication', data })

      return response
    }
  }
)
